import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { useCartValidations } from '@/shared/composables/useCartValidations';

import { useProductStore } from './product';
import { useProductOptionsStore } from './product-options';
import { useProductQuantitiesStore } from './product-quantities';

const {
  validateAddPayload,
  validateChangePayload,
  checkAndFetchPointsWhenNeeded
} = useCartValidations();

export const useCartStore = defineStore('cart', () => {
  const _cart = ref<ShopifyCart | null>(null);
  const _errors = ref<CartLineError[]>([]);

  const loadingCart = ref(false);
  const miniCartOpen = ref(false);
  const cart = computed(() => _cart.value);
  const errors = computed(() => _errors.value);

  watch(
    () => cart.value,
    async (newCart) => {
      if (newCart?.items) {
        checkAndFetchPointsWhenNeeded(newCart.items || []);
      }
    },
    { deep: true }
  );

  const clearErrors = () => {
    _errors.value = [];
  };

  const getCart = async () => {
    loadingCart.value = true;

    try {
      const localizedPaths = getLocalizedPaths();
      const response = await cartAPI.get(localizedPaths.CART);
      _cart.value = await response.json();
    } catch (error) {
      console.error(`Failed`, error);
    } finally {
      loadingCart.value = false;
    }
  };

  const addIndependentProductsToCart = async (items: ShopifyCartItem[]) => {
    const payload = { items };
    // no validation needed, only pattern related products are independent, those can't be point products
    return await addToCart(payload);
  };

  const addMainProductToCart = async () => {
    const productStore = useProductStore();
    const quantityStore = useProductQuantitiesStore();
    const optionsStore = useProductOptionsStore();

    const id: number | null =
      optionsStore.activeOption?.id ||
      (productStore.variantId
        ? (() => {
            const parsed = parseInt(productStore.variantId);
            return isNaN(parsed) ? null : parsed;
          })()
        : null);

    if (!id) return;

    const payload: ShopifyCartAddPayload = {
      items: [
        {
          id,
          quantity: quantityStore.quantity
        }
      ]
    };

    // add properties to the item to help point products be processed
    if (productStore.isPointProduct) {
      // when adding mainProduct, there's always only one item in the payload
      const itemBeingAdded = payload.items[0];

      //this property is not exposed to the user, thus using prefix '_'
      itemBeingAdded.properties = {
        _pointsPrice:
          (productStore.pointsPrice && productStore.pointsPrice + '') || '0'
      };

      if (window.Hobbii?.cartMessages) {
        payload.items[0].properties = {
          ...payload.items[0].properties,
          [window.Hobbii.cartMessages.pointProduct]:
            window.Hobbii?.cartMessages.paidWithPoints
        };
      }
    }

    // check if product is already in the cart, if product is a pattern
    if (productStore.isPattern) {
      const addedToCartAlready =
        cart.value?.items.find((item) => item.id == productStore.variantId)
          ?.quantity || 0;
      if (addedToCartAlready > 0) {
        throw new Error(window.Hobbii?.errorMessages.errorAlreadyAdded);
      }
    }

    await validateAddPayload(payload, cart.value?.items || []);
    return addToCart(payload);
  };

  const addToCart = async (payload: ShopifyCartAddPayload) => {
    const localizedPaths = getLocalizedPaths();
    const response = await cartAPI.post(localizedPaths.ADD, payload);

    if (response.ok) {
      return await getCart();
    }

    const error = await extractErrorFromResponse(
      response as ShopifyCartErrorResponse
    );

    // Shopify response with stock-related errors
    if (response.status === HTTP_RESPONSE_TYPE.SHOPIFY_STOCK_ERROR) {
      await getCart();

      throw error;
    }

    throw error;
  };

  const cartChangeQty = async (lineNumber: number, quantity: number) => {
    loadingCart.value = true;

    const payload = { line: lineNumber, quantity };
    try {
      await validateChangePayload(payload, cart.value?.items || []);
    } catch (error) {
      loadingCart.value = false;
      _errors.value.push({
        lineNumber,
        message: error instanceof Error ? error.message : String(error)
      });

      await getCart();
      return;
    }

    const localizedPaths = getLocalizedPaths();
    const response = await cartAPI.post(localizedPaths.CHANGE, payload);

    if (response.ok) {
      _cart.value = await response.json();
    } else {
      const error = await extractErrorFromResponse(
        response as ShopifyCartErrorResponse
      );

      // As cartChangeQty is called directly in on-click events, we store the error(s) in the store.
      // Changes to store.errors are watched & handled in frontend/entrypoints/cart.js
      _errors.value.push({ lineNumber, message: error.message });

      await getCart();
    }

    loadingCart.value = false;
  };

  return {
    loadingCart, // convert to computed when possible
    cart,
    miniCartOpen,
    errors,
    getCart,
    addToCart,
    addMainProductToCart,
    addIndependentProductsToCart,
    cartChangeQty,
    clearErrors
  };
});

const cartAPI = {
  get: (url: string) =>
    fetch(url, {
      cache: 'no-store'
    }),
  post: (
    url: string,
    payload: ShopifyCartAddPayload | ShopifyCartChangePayload
  ) =>
    fetch(url, {
      cache: 'no-store',
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
};

const HTTP_RESPONSE_TYPE = {
  SHOPIFY_STOCK_ERROR: 422,
  TOO_MANY_REQUESTS: 429,
  SERVICE_UNAVAILABLE: 503
};

const getLocalizedPaths = () => ({
  CART: window.Shopify.routes.root + 'cart.js',
  ADD: window.Shopify.routes.root + 'cart/add.js',
  CHANGE: window.Shopify.routes.root + 'cart/change.js'
});

const extractErrorFromResponse = async (response: ShopifyCartErrorResponse) => {
  // Shopify response with stock-related errors
  if (response.status === HTTP_RESPONSE_TYPE.SHOPIFY_STOCK_ERROR) {
    const error = await response.json();
    return new Error(error.description ?? error.message);
  }

  if (response.status === HTTP_RESPONSE_TYPE.TOO_MANY_REQUESTS) {
    return new Error(window.Hobbii?.errorMessages.tooManyRequests);
  }

  if (response.status === HTTP_RESPONSE_TYPE.SERVICE_UNAVAILABLE) {
    return new Error(window.Hobbii?.errorMessages.serviceUnavailable);
  }

  return new Error(window.Hobbii?.errorMessages.unknownErrorOccurred);
};
